import React from "react";
import { COLORS } from "../utils/constants";
import { translations, LanguageCode } from "../utils/translations";

interface AboutPageProps {
  lang?: LanguageCode;
}

const AboutPage: React.FC<AboutPageProps> = ({ lang = "en" }) => {
  const t = translations[lang].aboutPage;
  return (
    <div style={{ backgroundColor: "#fff" }}>
      {/* 1. Hero Header */}
      <header style={heroHeaderStyle}>
        <h1 style={heroTitleStyle}>
          {t.heroTitleLead} <br />
          <span style={{ color: COLORS.brandTeal }}>
            {t.heroTitleHighlight}
          </span>
        </h1>
        <p style={heroSubStyle}>
          {t.heroSub}
        </p>
      </header>

      {/* 2. Our Philosophy (Two-Column Layout) */}
      <section style={containerStyle}>
        <div style={splitGridStyle}>
          <div>
            <h2 style={sectionTitleStyle}>{t.whyTitle}</h2>
            <p style={paragraphStyle}>
              {t.whyP1}
            </p>
            <p style={paragraphStyle}>
              {t.whyP2Before}{" "}
              <strong>{t.whyP2Strong}</strong>
              {t.whyP2After}
            </p>
          </div>
          <div style={imagePlaceholderStyle}>
            {/* You can place a high-end agency team photo or an abstract growth graphic here */}
            <div style={{ color: COLORS.brandTeal, fontSize: "4rem" }}>📊</div>
            <p style={{ fontWeight: 600, color: COLORS.brandPurple }}>
              {t.dataDrivenGrowth}
            </p>
          </div>
        </div>
      </section>

      {/* 3. The Core Methodology (The "About" Substance) */}

      <section
        style={{
          ...containerStyle,
          backgroundColor: "#f9fafb",
          borderRadius: "40px",
          padding: "32px 5%",
        }}
      >
        <h2 style={{ ...sectionTitleStyle, textAlign: "center" }}>
          {t.methodologyTitle}
        </h2>
        <div style={methodologyGrid}>
          <div style={methodCard}>
            <h4 style={methodNumber}>01</h4>
            <h3 style={methodTitle}>{t.pillars[0].title}</h3>
            <p style={methodText}>
              {t.pillars[0].text}
            </p>
          </div>
          <div style={methodCard}>
            <h4 style={methodNumber}>02</h4>
            <h3 style={methodTitle}>{t.pillars[1].title}</h3>
            <p style={methodText}>
              {t.pillars[1].text}
            </p>
          </div>
          <div style={methodCard}>
            <h4 style={methodNumber}>03</h4>
            <h3 style={methodTitle}>{t.pillars[2].title}</h3>
            <p style={methodText}>
              {t.pillars[2].text}
            </p>
          </div>
        </div>
      </section>

      {/* 4. Ready to Partner CTA */}
      <section style={{ textAlign: "center", padding: "32px 6%" }}>
        <h2 style={sectionTitleStyle}>{t.ctaTitle}</h2>
        <button style={ctaButtonStyle}>{t.ctaButton}</button>
      </section>
    </div>
  );
};

// --- Styles ---

const heroHeaderStyle: React.CSSProperties = {
  padding: "32px 6%",
  textAlign: "center",
  background: `linear-gradient(180deg, rgba(113, 75, 103, 0.05) 0%, #ffffff 100%)`,
};

const heroTitleStyle: React.CSSProperties = {
  fontSize: "3.5rem",
  fontWeight: 900,
  color: COLORS.brandPurple,
  lineHeight: "1.1",
  marginBottom: "20px",
};

const heroSubStyle: React.CSSProperties = {
  fontSize: "1.2rem",
  color: "#718096",
  maxWidth: "700px",
  margin: "0 auto",
  lineHeight: "1.6",
};

const containerStyle: React.CSSProperties = {
  padding: "40px 6%",
  maxWidth: "1200px",
  margin: "0 auto",
};

const splitGridStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "40px",
  alignItems: "center",
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: "2.5rem",
  color: COLORS.brandPurple,
  fontWeight: 800,
  marginBottom: "16px",
};

const paragraphStyle: React.CSSProperties = {
  fontSize: "1.1rem",
  color: "#4A5568",
  lineHeight: "1.8",
  marginBottom: "20px",
};

const imagePlaceholderStyle: React.CSSProperties = {
  backgroundColor: "#f1f5f9",
  height: "400px",
  borderRadius: "30px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  border: "1px dashed #cbd5e1",
};

const methodologyGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
  gap: "24px",
  marginTop: "24px",
};

const methodCard: React.CSSProperties = {
  backgroundColor: "#fff",
  padding: "20px",
  borderRadius: "20px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
};

const methodNumber: React.CSSProperties = {
  fontSize: "3rem",
  fontWeight: 900,
  color: "rgba(1, 126, 132, 0.1)",
  marginBottom: "-25px",
  marginTop: "0",
};

const methodTitle: React.CSSProperties = {
  fontSize: "1.4rem",
  color: COLORS.brandPurple,
  fontWeight: 700,
  marginBottom: "15px",
};

const methodText: React.CSSProperties = {
  fontSize: "0.95rem",
  color: "#718096",
  lineHeight: "1.6",
};

const ctaButtonStyle: React.CSSProperties = {
  backgroundColor: COLORS.brandPurple,
  color: "#fff",
  padding: "18px 40px",
  borderRadius: "12px",
  border: "none",
  fontSize: "1.1rem",
  fontWeight: 700,
  cursor: "pointer",
  transition: "transform 0.2s ease",
};

export default AboutPage;
